/* 連線快照：武將專屬的畫面狀態（v0.4.2）
 * 這些狀態只有伺服器在算，客戶端只拿來畫：頭上的牌、背後的棺、坐騎、偽裝、回城條、元素印記、大招光環…
 * 之前快照沒帶，連線時看不到（楊修「解謎」按 W 頭上沒有牌就是這個）。
 * 時間都是世界時間 w.t，客戶端的 w.t 跟伺服器同步，直接比就好。
 * encode：伺服器端，沒有任何狀態就回傳 null（不佔快照）；decode：客戶端，每份快照都要呼叫（沒帶就清掉） */

type Any = any; // 單位物件在 sim 裡沒有完整型別

const r2 = (v: number) => Math.round(v * 100) / 100;
const live = (v: unknown, t: number) => typeof v === 'number' && v > t;

export function encodeVx(u: Any, t: number): Record<string, unknown> | null {
  const o: Record<string, unknown> = {};
  if (u.cardSel) o.cs = r2(u.cardSel.t0); // 楊修：抽牌開始的時間（客戶端用 w.t 算亮哪張）
  if (u.card) o.ck = u.card.type; // 楊修：鎖定的牌
  if (live(u.coffinT, t)) o.ct = r2(u.coffinT); // 龐德「抬櫬死戰」
  if (live(u.bulwarkT, t)) o.bw = r2(u.bulwarkT); // 江夏屏障
  if (live(u.sshieldT, t)) o.ss = r2(u.sshieldT); // 博望識伏
  if (live(u.mcT, t)) o.mc = r2(u.mcT); // 矯詔：倒戈中
  if (live(u.rollT, t)) o.rl = r2(u.rollT); // 鄧艾「裹氈滾下」
  if (u.bean) o.bn = 1; // 煮豆燃萁
  if (u.stolen && live(u.stolen.until, t)) o.sl = r2(u.stolen.until); // 曹丕「受禪」
  if (u.parry && live(u.parry.until, t)) o.pr = r2(u.parry.until); // 識破
  if (u.mounted) o.mt = 1; // 曹彰騎射：上馬
  if (u.disguise && u.disguise.spec) o.dg = [u.disguise.spec.id, u.disguise.name]; // 鍾會「效人書」：偽裝成誰
  if (u.recall) o.rc = [r2(u.recall.t), u.recall.dur]; // 回城條
  if (u.mk) o.mk = u.mk.el; // 元素印記
  if (u.clan && u.clan.cx != null) { const c = u.clan; o.cl = [c.cx | 0, c.cy | 0, r2(c.tx), r2(c.ty), c.w, r2(c.t0)]; } // 李典兵牆
  if (u.form) o.fm = [u.form, Math.round(u.range)]; // 形態（v0.5.0）：第幾個形態、射程（客戶端預測普攻距離用）
  if (u.gxS) { const s = u.gxS.filter((x: any) => x.until > t); if (s.length) o.gs = s.map((x: any) => [x.x | 0, x.y | 0, r2(x.until), x.k]); } // 郭脩的影子
  if (u.gate) { const g = u.gate; o.gt = [g.seg[0] | 0, g.seg[1] | 0, g.seg[2] | 0, g.seg[3] | 0, r2(g.until), r2(g.t0)]; } // 太史慈「持的出城」
  const ub = ultBuff(u);
  if (ub) o.ub = { sp: ub.src.spec.id, col: ub.col || null, as: ub.as || 0, ms: ub.ms || 0, aoe: ub.aoe ? ub.aoe.r || 160 : 0 }; // 大招增益的光環
  for (const k in o) return o;
  return null;
}

// 這個單位身上正在生效的大招增益（大招的增益以招式名稱當 id；跟 view.ultBuff 同一條規則）
function ultBuff(u: Any) {
  if (!u.buffs) return null;
  for (const b of u.buffs) if (!b.neg && b.src && b.src.spec && b.src.spec.skills && b.src.spec.skills[3] && b.id === b.src.spec.skills[3].name) return b;
  return null;
}

export function decodeVx(u: Any, v: Any, spec: (id: string) => Any) {
  v = v || {};
  u.cardSel = v.cs != null ? { t0: v.cs } : null;
  u.card = v.ck ? { type: v.ck } : null;
  u.coffinT = v.ct || 0; u.bulwarkT = v.bw || 0; u.sshieldT = v.ss || 0; u.mcT = v.mc || 0; u.rollT = v.rl || 0;
  u.bean = !!v.bn;
  u.stolen = v.sl ? { until: v.sl } : null;
  u.parry = v.pr ? { until: v.pr } : null;
  u.mounted = v.mt ? {} : null;
  u.disguise = v.dg ? { spec: spec(v.dg[0]), name: v.dg[1] } : null;
  u.recall = v.rc ? { t: v.rc[0], dur: v.rc[1] } : null;
  u.mk = v.mk ? { el: v.mk } : null;
  u.clan = v.cl ? { cx: v.cl[0], cy: v.cl[1], tx: v.cl[2], ty: v.cl[3], w: v.cl[4], t0: v.cl[5] } : null;
  const forms = u.spec && u.spec.forms;
  if (forms) { // 形態：換成那個形態的設定（技能列、兵器、普攻射程跟著換）；沒帶就是預設形態
    const f = v.fm ? v.fm[0] : 0, fs = forms[f] || forms[0];
    if (u.spec !== fs.spec) { u.spec = fs.spec; u.ranged = !!fs.ranged; }
    u.form = f;
    if (v.fm) u.range = v.fm[1]; else if (u.range == null || (u.ranged ? u.range < 300 : u.range > 300)) u.range = u.ranged ? 430 : 90;
  }
  u.gxS = v.gs ? v.gs.map((s: any) => ({ x: s[0], y: s[1], until: s[2], k: s[3] })) : null;
  u.gate = v.gt ? { seg: v.gt.slice(0, 4), until: v.gt[4], t0: v.gt[5] } : null;
  if (v.ub) { // 畫面層用 view.ultBuff 認大招增益：還原成同樣的形狀
    const s = spec(v.ub.sp);
    u.buffs = s && s.skills && s.skills[3] ? [{ id: s.skills[3].name, src: { spec: s }, col: v.ub.col, as: v.ub.as, ms: v.ub.ms, aoe: v.ub.aoe ? { r: v.ub.aoe } : null }] : null;
  } else u.buffs = null;
}
